import { ReactNode } from 'react';
import type {
  ArrayPath,
  Control,
  FieldArrayPathValue,
  FieldErrors,
  FieldPath,
  FieldValue,
  FieldValues,
  RegisterOptions,
  UseFormGetValues
} from 'react-hook-form';
import type { AnyObjectSchema } from 'yup';

import type { Person } from '@/types/person';
import type { OptionType, defaultOptionTypes } from '@/ui/components/Form/Select/types';

export type PersonInPlaceEditorProps = {
  disableEdit?: boolean;
  fields: Field<Person>[] | ArrayField<Person>;
  children: React.ReactNode;
  schema?: AnyObjectSchema;
  sectionEditor?: boolean;
  subsectionEditor?: boolean;
};

export interface FieldEditorProps<T extends FieldValues> {
  field: Field<T>;
  isMutable?: boolean;
  value: FieldValue<T>;
  // [TODO]: extract defaultOptionTypes into an input value type
  onChange: (value: defaultOptionTypes) => void;
}

export interface ArrayFieldEditorProps<T extends FieldValues> {
  control: Control<T>;
  fieldKey: ArrayPath<T>;
  fields: FieldOfArrayField<T>[];
  newObject: NewObject<T>;
  errors: FieldErrors<T>;
  getFormValue: UseFormGetValues<T>;
  /** If set, confirms with this message before deleting the last remaining row. */
  deleteConfirmMessage?: string;
}

/**
 * Either a fixed default row, or a factory that computes one from the
 * array's current (unsaved) rows, e.g. to default `default: true` when
 * it's about to become the only row.
 */
export type NewObject<T extends FieldValues> =
  | ArrayFieldValue<T, ArrayPath<T>>
  | ((
      currentRows: (ArrayFieldValue<T, ArrayPath<T>> & { _delete?: boolean })[]
    ) => ArrayFieldValue<T, ArrayPath<T>>);

export type ArrayFieldValue<
  T extends FieldValues,
  TFieldArrayName extends ArrayPath<T>
> = NonNullable<FieldArrayPathValue<T, TFieldArrayName>>[number];

export interface ArrayFieldControllerProps<T extends FieldValues> {
  control: Control<T>;
  index: number;
  isMutable: boolean;
  field: FieldOfArrayField<T>;
}

export enum FieldTypes {
  BOOLEAN = 'boolean',
  CURRENCY = 'currency',
  DATE = 'date',
  FILE = 'file',
  HIDDEN = 'hidden',
  NUMBER = 'number',
  PERCENTAGE = 'percentage',
  RADIO = 'radio',
  RICH_TEXT = 'rich-text',
  SELECT = 'select',
  TEXT = 'text',
  TEXTAREA = 'textarea'
}

export type Field<T extends FieldValues> = SingleField<T> | FieldWithOptions<T>;

interface BaseField<T extends FieldValues> {
  additionalCaption?: { label?: string; value?: string | ReactNode };
  alwaysSend?: boolean;
  className?: string;
  control?: string;
  hidden?: boolean;
  isLoadingOptions?: boolean;
  key: FieldPath<T>;
  label: string | ReactNode;
  onSearch?: (search: string) => void;
  placeholder?: string;
  rules?: RegisterOptions;
  readOnly?: boolean;
}

interface SingleField<T extends FieldValues> extends BaseField<T> {
  type:
    | FieldTypes.BOOLEAN
    | FieldTypes.CURRENCY
    | FieldTypes.PERCENTAGE
    | FieldTypes.DATE
    | FieldTypes.FILE
    | FieldTypes.HIDDEN
    | FieldTypes.NUMBER
    | FieldTypes.RICH_TEXT
    | FieldTypes.TEXT
    | FieldTypes.TEXTAREA;
}

interface FieldWithOptions<T extends FieldValues> extends BaseField<T> {
  type: FieldTypes.RADIO | FieldTypes.SELECT;
  options:
    | { value: defaultOptionTypes; label: string }[]
    | (() => Promise<
        {
          label: string;
          value: defaultOptionTypes;
        }[]
      >);
  /** Optionally override how each option's label renders in a SELECT menu. */
  formatOptionLabel?: (option: OptionType) => ReactNode;
}

export interface ArrayField<T extends FieldValues> {
  key: ArrayPath<T>;
  fields: FieldOfArrayField<T>[];
  newObject: NewObject<T>;
  /** If set, confirms with this message before deleting the last remaining row. */
  deleteConfirmMessage?: string;
}

export type FieldOfArrayField<T extends FieldValues> =
  SingleFieldOfArrayField<T> | FieldOfArrayFieldWithOptions<T>;

interface ArrayFieldBaseField<T extends FieldValues> {
  key: (idx: number) => FieldPath<T>;
  label: string;
  hidden?: boolean;
}

interface SingleFieldOfArrayField<T extends FieldValues> extends ArrayFieldBaseField<T> {
  type:
    | FieldTypes.BOOLEAN
    | FieldTypes.DATE
    | FieldTypes.CURRENCY
    | FieldTypes.PERCENTAGE
    | FieldTypes.HIDDEN
    | FieldTypes.NUMBER
    | FieldTypes.RICH_TEXT
    | FieldTypes.TEXT
    | FieldTypes.TEXTAREA;
}

interface FieldOfArrayFieldWithOptions<T extends FieldValues> extends ArrayFieldBaseField<T> {
  type: FieldTypes.RADIO | FieldTypes.SELECT;
  options:
    | { value: defaultOptionTypes; label: string }[]
    | (() => Promise<
        {
          label: string;
          value: defaultOptionTypes;
        }[]
      >);
}
